import React, { useContext, useEffect } from 'react';
import HamQuest from '../../api/HamQuestGiver';
import { QuestRoute } from '../../api/QuestRoutes';
import Button from '../../forms/Button';
import Input from '../../forms/Input';
import FormSetting from '../../layout/FormSetting';
import Select from '../../forms/Select';
import { HSOptions } from '../../../constants/HSOptions';
import Row from '../../layout/Row';
import { userRoles } from '../../RoleProtected';
import { UserContexts } from '../../stateProvider/UsersContext';
import ModalFactory from '../../Modal';
import Checkbox from '../../forms/Checkbox';
import GetState from '../../GetState';
import UpgradeAndAutoSelect from '../../forms/formComponents/UpgradeAndAutoSelect';
import { Features, HasFeature } from '../../RequireFeature';
import { FormContext } from '../../forms/Form';

const WCUserSettingsForm = () => {
    const { values, setValues, editing } = useContext(FormContext);
    const hasNewMaxUses = HasFeature(Features.NewMaxUses);

    useEffect(() => {
        const app = values.appMaxUses !== '' && values.appMaxUses != null ? Number(values.appMaxUses) : null;
        const pass = values.passMaxUses !== '' && values.passMaxUses != null ? Number(values.passMaxUses) : null;
        if (app !== null && pass !== null && !isNaN(app) && !isNaN(pass)) {
            setValues(v => ({ ...v, maxRedemptions: String(app + pass) }));
        }
    }, [values.appMaxUses, values.passMaxUses]);

    return (
        <>
            {hasNewMaxUses ? (
                <>
                    <Row wrap>
                        <Input
                            label='App Max Uses'
                            name='appMaxUses'
                            type='number'
                            min={1}
                            disabled={editing}
                            tooltipText='Maximum App redemptions that can be vended in a given billing period. Leave blank for no limit.'
                        />
                    </Row>
                    <Row wrap>
                        <Input
                            label='Pass Max Uses'
                            name='passMaxUses'
                            type='number'
                            min={1}
                            disabled={editing}
                            tooltipText='Maximum Pass redemptions that can be vended in a given billing period. Leave blank for no limit.'
                        />
                    </Row>
                    <Row wrap>
                        <Input
                            label='Maximum Redemptions'
                            name='maxRedemptions'
                            type='number'
                            min={1}
                            disabled={editing}
                            tooltipText='Maximum total redemptions (Pass + App) per billing period. Auto-fills as the sum of App Max Uses and Pass Max Uses when both are set.'
                        />
                    </Row>
                    <Row wrap>
                        <Input
                            label='Teller Reuse Limit'
                            name='tellerReuseLimit'
                            type='number'
                            min={1}
                            disabled={editing}
                            tooltipText='Maximum number of times in one Calendar Day that a customer can redeem at the teller.'
                        />
                    </Row>
                    <Row wrap>
                        <Input
                            label='Timer Max Uses'
                            name='timerMaxUses'
                            type='number'
                            min={1}
                            disabled={editing}
                        />
                    </Row>
                    <Row wrap>
                        <Input
                            label='Timer Reuse Limit'
                            name='timerReuseLimit'
                            type='number'
                            min={1}
                            disabled={editing}
                            tooltipText='Maximum number of times in one Calendar Day that a customer can redeem at the timers.'
                        />
                    </Row>
                </>
            ) : (
                <>
                    <Row wrap>
                        <Select
                            label='Max Use Period'
                            name='passMaxUsePeriod'
                            options={HSOptions.washClub.period}
                            required
                        />
                        {(values.passMaxUsePeriod === 'Unlimited' || values.passMaxUsePeriod === 'UseDefault') ? (
                            <Input
                                label='Pass Max Uses'
                                disabled
                                required
                            />
                        ) : (
                            <Input
                                label='Pass Max Uses'
                                name='passMaxUses'
                                type='number'
                                min={1}
                                disabled={values.passMaxUsePeriod === 'Unlimited' || values.passMaxUsePeriod === 'UseDefault' || editing}
                                required
                            />
                        )}
                    </Row>

                    <Row wrap>
                        <Select
                            label='App Scan Max Use Period'
                            name='appIdPassMaxUsePeriod'
                            options={HSOptions.washClub.period}
                            required
                        />
                        {(values.appIdPassMaxUsePeriod === 'Unlimited' || values.appIdPassMaxUsePeriod === 'UseDefault') ? (
                            <Input
                                label='App Scan Max Uses'
                                disabled
                                required
                            />
                        ) : (
                            <Input
                                label='App Scan Max Uses'
                                name='appIdPassMaxUses'
                                type='number'
                                min={1}
                                disabled={values.appIdPassMaxUsePeriod === 'Unlimited' || values.appIdPassMaxUsePeriod === 'UseDefault' || editing}
                                required
                            />
                        )}
                    </Row>

                    <Row wrap>
                        <Select
                            label='App Scan Max Timer Use Period'
                            name='appIdPassMaxTimerUsePeriod'
                            options={HSOptions.washClub.period}
                            required
                        />
                        {(values.appIdPassMaxTimerUsePeriod === 'Unlimited' || values.appIdPassMaxTimerUsePeriod === 'UseDefault') ? (
                            <Input
                                label='App Scan Max Timer Uses'
                                disabled
                                required
                            />
                        ) : (
                            <Input
                                label='App Scan Max Timer Uses'
                                name='appIdPassMaxTimerUses'
                                type='number'
                                min={1}
                                disabled={values.appIdPassMaxTimerUsePeriod === 'Unlimited' || values.appIdPassMaxTimerUsePeriod === 'UseDefault' || editing}
                                required
                            />
                        )}
                    </Row>

                    <Row align='flex-end' wrap>
                        <Checkbox
                            label='Use Default Pass Reuse Time'
                            name='useDefaultPassReuseTime'
                        />
                        {values.useDefaultPassReuseTime ?
                            <Input
                                label='Pass Reuse Inhibit Time (min)'
                                disabled
                                required
                            /> :
                            <Input
                                label='Pass Reuse Inhibit Time (min)'
                                name='passReuseInhibitTime'
                                type='number'
                                disabled={values.useDefaultPassReuseTime || editing}
                                required
                            />
                        }
                    </Row>
                </>
            )}

            <UpgradeAndAutoSelect autoSelectFieldName='autoSelect' />

            <Row align='flex-end' wrap>
                <Select
                    label='Kiosk Receipt Mode'
                    name='receiptMode'
                    options={HSOptions.washClub.receiptMode}
                    required
                />
            </Row>
        </>
    );
};

const WashClubSettings = ({ ...props }) => {
    const context = useContext(UserContexts);

    const [usersWCSettings, refreshWCSettings] = GetState(QuestRoute.user.washClubSettings(context.user.customerId));

    const resetUserWCSettings = (user) => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Are you sure?`,
                body: (<span>Are you sure you want to restore Wash Club default settings for {context.user.firstName}?</span>),
                type: 'warning',
            },
            promise: {
                title: 'Restoring Defaults',
                body: `Restoring, please wait.`,
                callback: () => HamQuest.delete(QuestRoute.user.washClubSettings(context.user.customerId))
            },
            confirmed: {
                title: 'Success',
                body: `${context.user.firstName}'s settings have been set to default.`,
                type: 'success'
            },
            finally: () => refreshWCSettings()
        })
    }

    return (
        <FormSetting
            name='washClubUserSettings'
            label={`User's Wash Club Settings`}
            onSubmit={e => {
                if (e.allowUpgrades == true || e.allowUpgrades == false)
                    e.allowUpgrades = e.allowUpgrades.toString().charAt(0).toUpperCase() + e.allowUpgrades.toString().slice(1).toLowerCase();
                if (e.autoSelect == true || e.autoSelect == false)
                    e.autoSelect = e.autoSelect.toString().charAt(0).toUpperCase() + e.autoSelect.toString().slice(1).toLowerCase();
                return HamQuest.put(QuestRoute.user.washClubSettings(context.user.customerId), { ...e }).then(() => refreshWCSettings())
            }}
            inputs={{ ...usersWCSettings }}
            controls={[<Button label='Restore Default Settings' onClick={resetUserWCSettings} />]}
            roles={userRoles.editCustomers}
            text='Update'
            editable
        >
            {() => <WCUserSettingsForm />}
        </FormSetting>
    );
}

export default WashClubSettings;
